import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ExerciseCommonFields from '../../components/Exercise/ExerciseCommonFields';
import ExerciseMaterialSelector from '../../components/Exercise/ExerciseMaterialSelector';
import MathArit2Operand1PhaseConfig from '../../components/Exercise/MathArit2Operand1Phase/MathArit2Operand1PhaseConfig';
import { ExerciseUpsertPayload, getExercise, updateExercise } from '../../services/exerciseService'; // Import getExercise and updateExercise services
import Breadcrumb from '../../components/Breadcrumb';
import MathAritRoundingConfig from '../../components/Exercise/MathAritRounding/MathAritRoundingConfig';
import FillInBlanksConfig from '../../components/Exercise/FillInTheBlanks/FillInBlanksConfig';
import PairingConfig from '../../components/Exercise/Pairing/PairingConfig';
import MaterialBasedConfig from '../../components/Exercise/MaterialBased/MaterialBasedConfig';
import MaterialBasedMultichoiceConfig from '../../components/Exercise/MaterialBasedMultichoice/MaterialBasedMultichoiceConfig';
import SortToBoxesConfig from '../../components/Exercise/SortToBoxes/SortToBoxesConfig';
import OrderingConfig from '../../components/Exercise/Ordering/OrderingConfig';
import AuthoringAssistantPanel from '../../components/AI/AuthoringAssistantPanel';
import { AuthoringAssistantGeneratedDraftResponse } from '../../services/authoringAssistantService';

const ExerciseEditPage: React.FC = () => {
  const { lessonId, exerciseId } = useParams<{ lessonId: string; exerciseId: string }>();
  const { courseId } = useParams<{ courseId: string }>(); // Add courseId for navigation
  const navigate = useNavigate();
  const parsedLessonId = lessonId ? parseInt(lessonId, 10) : null;
  const parsedExerciseId = exerciseId ? parseInt(exerciseId, 10) : null;
  const parsedCourseId = courseId ? parseInt(courseId, 10) : undefined;
  const [exerciseType, setExerciseType] = useState<string | null>(null);
  const [exerciseConfig, setExerciseConfig] = useState<any>(null);
  const [configRenderVersion, setConfigRenderVersion] = useState(0);
  
  // Common exercise fields state
  const [commonFields, setCommonFields] = useState({
    instructions: '',
    additionalInfo: '',
    order: 1,
    maxScore: 100,
    requiresManualChecking: false,
    allowReset: true,
    showCorrectAnswers: true,
  });

  useEffect(() => {
    const fetchExercise = async () => {
      if (parsedLessonId && parsedExerciseId) {
        try {
          const response = await getExercise(parsedLessonId, parsedExerciseId);
          const exerciseData = response.data;
          const parsedConfig = JSON.parse(exerciseData.config);

          setExerciseType(exerciseData.type);
          setCommonFields({
            instructions: exerciseData.instructions,
            additionalInfo: exerciseData.additionalInfo,
            order: exerciseData.order,
            maxScore: exerciseData.maxScore,
            requiresManualChecking: exerciseData.requiresManualChecking || false,
            allowReset: exerciseData.allowReset !== undefined ? exerciseData.allowReset : true,
            showCorrectAnswers: exerciseData.showCorrectAnswers !== undefined ? exerciseData.showCorrectAnswers : true,
          });
          setExerciseConfig({
            ...parsedConfig,
            materialId: exerciseData.materialId ?? parsedConfig.materialId,
            showMaterialToStudent:
              exerciseData.showMaterialToStudent ?? parsedConfig.showMaterialToStudent ?? false,
          });
        } catch (error) {
          console.error('Failed to load exercise:', error);
        }
      }
    };

    fetchExercise();
  }, [parsedLessonId, parsedExerciseId]);

  const handleCommonFieldChange = (field: string, value: any) => {
    setCommonFields((prevFields) => ({
      ...prevFields,
      [field]: value,
    }));
  };

  const handleConfigChange = (config: any) => {
    setExerciseConfig((previousConfig: any) => ({
      ...previousConfig,
      ...config,
    }));
  };

  const handleMaterialChange = (materialId?: number, showMaterialToStudent?: boolean) => {
    setExerciseConfig((previousConfig: any) => ({
      ...previousConfig,
      materialId,
      showMaterialToStudent,
    }));
  };

  const usesBuiltInMaterialSelection = [
    'material_based_ai_assisted',
    'material_based_multichoice',
    'reading_comprehension_multichoice',
    'true_false',
  ].includes(exerciseType || '');

  const handleApplyDraft = (draft: AuthoringAssistantGeneratedDraftResponse) => {
    if (!draft.exercise) {
      return;
    }

    setCommonFields((previousFields) => ({
      ...previousFields,
      instructions: draft.exercise?.instructions || previousFields.instructions,
      additionalInfo: draft.exercise?.additionalInfo || previousFields.additionalInfo,
    }));

    if (draft.exercise.configPatch && typeof draft.exercise.configPatch === 'object') {
      setExerciseConfig((previousConfig: any) => ({
        ...previousConfig,
        ...draft.exercise?.configPatch,
      }));
      setConfigRenderVersion((previousVersion) => previousVersion + 1);
    }
  };

  const handleSaveExercise = async () => {
    if (!parsedLessonId || !parsedExerciseId || !exerciseType || !exerciseConfig) return;

    console.log('Edit page - commonFields before save:', commonFields);

    const { materialId, ...configForSave } = exerciseConfig;

    const exerciseData: ExerciseUpsertPayload = {
      type: exerciseType,
      config: JSON.stringify(configForSave), // Convert config to string
      instructions: commonFields.instructions,
      additionalInfo: commonFields.additionalInfo,
      order: commonFields.order,
      maxScore: commonFields.maxScore,
      requiresManualChecking: commonFields.requiresManualChecking,
      allowReset: commonFields.allowReset,
      showCorrectAnswers: commonFields.showCorrectAnswers,
      materialId,
      showMaterialToStudent: configForSave.showMaterialToStudent,
    };

    console.log('Edit page - exerciseData before sending to backend:', exerciseData);

    try {
      await updateExercise(parsedLessonId, parsedExerciseId, exerciseData);
      navigate(`/course/${courseId}/lesson/${lessonId}`); // Navigate back to the lesson page
    } catch (error) {
      console.error('Failed to save exercise:', error);
    }
  };

  const renderExerciseConfig = () => {
    switch (exerciseType) {
      case 'math_arit_2operand_1phase':
        return <MathArit2Operand1PhaseConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} />;
      case 'math_arit_rounding_precision':
        return <MathAritRoundingConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} />;
      case 'fill_in_the_blanks':
        return <FillInBlanksConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} />;
      case 'pairing':
        return <PairingConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} />;
      case 'material_based_ai_assisted':
        if (!parsedLessonId) {
          return <Typography variant="body1">Oppitunnin tunniste puuttuu</Typography>;
        }
        return <MaterialBasedConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} lessonId={parsedLessonId} />;
      case 'material_based_multichoice':
        if (!parsedLessonId) {
          return <Typography variant="body1">Oppitunnin tunniste puuttuu</Typography>;
        }
        return <MaterialBasedMultichoiceConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} lessonId={parsedLessonId} />;
      case 'reading_comprehension_multichoice':
        if (!parsedLessonId) {
          return <Typography variant="body1">Oppitunnin tunniste puuttuu</Typography>;
        }
        return <MaterialBasedMultichoiceConfig
          initialConfig={exerciseConfig}
          onConfigChange={handleConfigChange}
          lessonId={parsedLessonId}
          exerciseType="reading_comprehension_multichoice"
          generationProfile="reading_comprehension_fi"
        />;
      case 'true_false':
        if (!parsedLessonId) {
          return <Typography variant="body1">Oppitunnin tunniste puuttuu</Typography>;
        }
        return <MaterialBasedMultichoiceConfig
          initialConfig={exerciseConfig}
          onConfigChange={handleConfigChange}
          lessonId={parsedLessonId}
          exerciseType="true_false"
          generationProfile="true_false"
          fixedOptionCount={2}
        />;
      case 'math_arit_2operand_2phase':
        return <MathArit2Operand1PhaseConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} />;
      case 'sort_to_boxes':
        return <SortToBoxesConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} />;
      case 'ordering':
        return <OrderingConfig initialConfig={exerciseConfig} onConfigChange={handleConfigChange} />;
      default:
        return <Typography variant="body1">Harjoituksen tyyppiä ei tunnistettu</Typography>;
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      {courseId && <Breadcrumb courseId={courseId} lessonId={lessonId} exerciseId={exerciseId} />}

      <Box
        mt={3}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          flexDirection: { xs: 'column', xl: 'row' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Typography variant="h4" gutterBottom>
            Muokkaa harjoitusta
          </Typography>

          {exerciseType && (
            <Box mb={4}>
              <ExerciseCommonFields
                instructions={commonFields.instructions}
                additionalInfo={commonFields.additionalInfo}
                order={commonFields.order}
                maxScore={commonFields.maxScore}
                requiresManualChecking={commonFields.requiresManualChecking}
                allowReset={commonFields.allowReset}
                showCorrectAnswers={commonFields.showCorrectAnswers}
                onChange={handleCommonFieldChange}
              />
            </Box>
          )}

          {exerciseType && parsedLessonId && !usesBuiltInMaterialSelection && (
            <Box mb={4}>
              <ExerciseMaterialSelector
                lessonId={parsedLessonId}
                materialId={exerciseConfig?.materialId}
                showMaterialToStudent={exerciseConfig?.showMaterialToStudent}
                onChange={handleMaterialChange}
              />
            </Box>
          )}

          {exerciseType && (
            <Box mb={4} key={`exercise-config-${exerciseType}-${configRenderVersion}`}>
              {renderExerciseConfig()}
            </Box>
          )}

          {exerciseType && (
            <Box mb={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveExercise}
              >
                Tallenna muutokset
              </Button>
            </Box>
          )}
        </Box>

        <AuthoringAssistantPanel
          courseId={parsedCourseId}
          lessonId={parsedLessonId || undefined}
          exerciseId={parsedExerciseId || undefined}
          draftExercise={{
            type: exerciseType,
            instructions: commonFields.instructions,
            additionalInfo: commonFields.additionalInfo,
            config: exerciseConfig,
          }}
          draftTarget="exercise"
          draftDisabled={!exerciseType}
          onApplyDraft={handleApplyDraft}
        />
      </Box>
    </Container>
  );
};

export default ExerciseEditPage;
